/**
 * 3D incident/community flythrough controller (#179 Cesium-wow).
 *
 * Operator-triggered ONLY (no marker/billboard animation — the flythrough is
 * camera motion, explicitly outside the Law-6 marker-animation budget): from an
 * incident's or community's detail panel the camera descends to an oblique
 * approach, sweeps a full orbit around the site over photorealistic 3D
 * context, then restores the operator's prior view.
 *
 * 3D context is ON-DEMAND and fully released when the flythrough ends — nothing
 * persistent joins the COP (design-council PART IX):
 *   - Cesium World Terrain via the shared ref-counted terrainContext (#228), so
 *     it coexists with the flood-inundation / viewshed layers and the LAST
 *     holder restores the operator's globe.
 *   - Google Photorealistic 3D Tiles (streamed through the existing Cesium ion
 *     token — runtime-verified, ion asset 2275207) via the ref-counted
 *     photorealContext. HONEST DEGRADE: if Google tiles fail to stream we fall
 *     back to Cesium OSM Buildings (managed locally, flood-layer idiom); if
 *     those fail too, the flythrough flies over plain terrain. Failures log
 *     once and never throw into the render loop (#227 guard stays clean).
 *
 * Flight legs use the shared settle grammar (#252, lib/cameraFlight.ts): each
 * leg resolves on real complete/cancel, and a wall-clock backstop *lands* a
 * starved flight (cancelFlight + setView to the exact keyframe) instead of
 * abandoning it mid-arc — so a throttled render loop (backgrounded tab,
 * headless browser) can never wedge the flythrough mid-path holding tiles
 * forever, and the exit always restores the operator's exact prior view.
 */
import * as Cesium from 'cesium';
import {
  flythroughKeyframes,
  FLYTHROUGH_RESTORE_DURATION_S,
  type FlythroughKeyframe,
  type FlythroughTarget,
} from './flythroughPath';
import { acquireWorldTerrain, releaseWorldTerrain } from './lib/terrainContext';
import {
  captureCameraPose,
  flyToSettled,
  type CameraPose,
  type SettleCamera,
} from './lib/cameraFlight';
import {
  acquirePhotorealTiles,
  releasePhotorealTiles,
  type PhotorealTileset,
  type PhotorealViewer,
} from './lib/photorealContext';

export type FlythroughEndReason = 'completed' | 'stopped' | 'destroyed';

/** What streamed under the flythrough — surfaced for logs/QA honesty. */
export type FlythroughContextMode = 'photoreal' | 'osm-buildings' | 'terrain-only';

/** The imperative handle MapView exposes to App (floodInundation idiom). */
export interface FlythroughController {
  /** Start a flythrough around `target`. No-op if one is already running.
   *  `onEnded` fires exactly once when the flythrough finishes, is stopped, or
   *  the viewer unmounts. */
  start(target: FlythroughTarget, onEnded?: (reason: FlythroughEndReason) => void): void;
  /** Cancel a running flythrough: flies back to the captured prior view and
   *  releases the 3D context. No-op when idle. */
  stop(): void;
  isActive(): boolean;
}

export interface FlythroughHandle extends FlythroughController {
  /** Synchronous teardown for viewer unmount — releases everything, no flight. */
  destroy(): void;
  /** What 3D context the current/last run streamed (QA/debug honesty). */
  contextMode(): FlythroughContextMode;
}

/** The viewer slice the controller drives — structural so unit tests run on a
 *  fake (no WebGL). The real Cesium.Viewer satisfies it. */
export interface FlythroughViewer extends PhotorealViewer {
  camera: SettleCamera;
  scene: PhotorealViewer['scene'] & { requestRender?: () => void };
}

/** Injectable async factories (real Cesium calls by default) so the full
 *  start → orbit → restore → release path is unit-testable. */
export interface FlythroughDeps {
  createTerrain?: () => Promise<Cesium.TerrainProvider>;
  createPhotorealTiles?: () => Promise<PhotorealTileset>;
  createFallbackBuildings?: () => Promise<PhotorealTileset>;
}

export function installFlythrough(
  viewer: FlythroughViewer,
  deps: FlythroughDeps = {},
): FlythroughHandle {
  const createTerrain = deps.createTerrain ?? (() => Cesium.createWorldTerrainAsync());
  const createPhotorealTiles =
    deps.createPhotorealTiles ??
    (() =>
      Cesium.createGooglePhotorealistic3DTileset({
        onlyUsingWithGoogleGeocoder: true,
      }) as Promise<PhotorealTileset>);
  const createFallbackBuildings =
    deps.createFallbackBuildings ?? (() => Cesium.createOsmBuildingsAsync() as Promise<PhotorealTileset>);

  let active = false;
  let gen = 0;
  let priorPose: CameraPose | null = null;
  let onEnded: ((reason: FlythroughEndReason) => void) | null = null;
  let mode: FlythroughContextMode = 'terrain-only';

  // What this controller currently holds (one hold each, per the context
  // modules' pairing contract).
  let holdingTerrain = false;
  let holdingTiles = false;
  /** OSM Buildings fallback — flythrough-only, so managed locally (the flood
   *  layer's own OSM tileset is separate and untouched). */
  let fallbackBuildings: PhotorealTileset | null = null;

  const flyKeyframe = (kf: FlythroughKeyframe): Promise<unknown> =>
    flyToSettled(
      viewer,
      {
        destination: Cesium.Cartesian3.fromDegrees(kf.lon, kf.lat, kf.heightM),
        orientation: {
          heading: Cesium.Math.toRadians(kf.headingDeg),
          pitch: Cesium.Math.toRadians(kf.pitchDeg),
          roll: 0,
        },
      },
      kf.durationS,
    );

  /** Release every 3D hold (idempotent — safe from any path). */
  const releaseAll = (): void => {
    if (holdingTiles) {
      holdingTiles = false;
      releasePhotorealTiles(viewer);
    }
    if (fallbackBuildings) {
      const b = fallbackBuildings;
      fallbackBuildings = null;
      if (!viewer.isDestroyed?.()) {
        const removed = viewer.scene.primitives.remove(b);
        if (!removed && !b.isDestroyed()) b.destroy();
      }
    }
    if (holdingTerrain) {
      holdingTerrain = false;
      // terrainContext is typed against the real Viewer; the structural slice
      // carries everything it touches at runtime.
      releaseWorldTerrain(viewer as unknown as Cesium.Viewer);
    }
  };

  /** Fire the end callback exactly once. */
  const emitEnded = (reason: FlythroughEndReason): void => {
    const cb = onEnded;
    onEnded = null;
    active = false;
    cb?.(reason);
  };

  /** Restore the operator's prior view, then release the 3D context. Tiles stay
   *  up DURING the restore flight (no pop mid-flight); the release lands when
   *  the flight ends. A starved restore flight settles by setView, so the end
   *  pose is the captured pose exactly (#252). */
  const finish = async (reason: FlythroughEndReason): Promise<void> => {
    const pose = priorPose;
    priorPose = null;
    if (pose && !viewer.isDestroyed?.()) {
      await flyToSettled(
        viewer,
        {
          destination: pose.destination,
          orientation: { heading: pose.heading, pitch: pose.pitch, roll: pose.roll },
        },
        FLYTHROUGH_RESTORE_DURATION_S,
      );
    }
    releaseAll();
    emitEnded(reason);
  };

  /** Acquire 3D context + fly the path. Generation-guarded: a stop()/destroy()
   *  bumps `gen`, so every await re-checks before touching the viewer. */
  const run = async (myGen: number, target: FlythroughTarget): Promise<void> => {
    // 1. World Terrain (shared, ref-counted). Degrade: fly over whatever globe
    //    the operator already has.
    try {
      const terrain = await createTerrain();
      if (myGen !== gen) return;
      acquireWorldTerrain(viewer as unknown as Cesium.Viewer, terrain);
      holdingTerrain = true;
    } catch (err) {
      console.error('flythrough: World Terrain unavailable (ion token?) — flying without it', err);
    }
    if (myGen !== gen) return;

    // 2. Photoreal context: Google Photorealistic 3D Tiles first, OSM Buildings
    //    fallback, plain terrain last. All failures are logged, none throw.
    try {
      const tiles = await createPhotorealTiles();
      if (myGen !== gen) {
        if (!tiles.isDestroyed()) tiles.destroy();
        return;
      }
      const installed = acquirePhotorealTiles(viewer, tiles);
      holdingTiles = true;
      if (installed !== tiles && !tiles.isDestroyed()) tiles.destroy();
      mode = 'photoreal';
    } catch (err) {
      console.warn(
        'flythrough: Google Photorealistic 3D Tiles unavailable — degrading to OSM Buildings',
        err,
      );
      try {
        const buildings = await createFallbackBuildings();
        if (myGen !== gen) {
          if (!buildings.isDestroyed()) buildings.destroy();
          return;
        }
        viewer.scene.primitives.add(buildings);
        fallbackBuildings = buildings;
        mode = 'osm-buildings';
      } catch (err2) {
        console.warn('flythrough: OSM Buildings unavailable too — flying over plain terrain', err2);
        mode = 'terrain-only';
      }
    }
    if (myGen !== gen) return;

    // 3. Fly the path. Re-check the generation between legs so a stop() lands
    //    at the next keyframe boundary at the latest.
    for (const kf of flythroughKeyframes(target)) {
      if (myGen !== gen) return;
      await flyKeyframe(kf);
    }
    if (myGen !== gen) return;
    gen++; // this run owns the finish — a late stop() must not double-finish
    await finish('completed');
  };

  return {
    start(target, ended): void {
      if (active || viewer.isDestroyed?.()) return;
      active = true;
      mode = 'terrain-only';
      onEnded = ended ?? null;
      priorPose = captureCameraPose(viewer.camera);
      const myGen = ++gen;
      void run(myGen, target);
    },
    stop(): void {
      if (!active) return;
      gen++; // cancel the in-flight run at its next checkpoint
      void finish('stopped');
    },
    isActive(): boolean {
      return active;
    },
    contextMode(): FlythroughContextMode {
      return mode;
    },
    destroy(): void {
      gen++;
      priorPose = null;
      releaseAll();
      if (active) emitEnded('destroyed');
    },
  };
}
